import {Grid, InputAdornment, InputLabel, makeStyles, MenuItem, Typography} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {isLoaded} from 'Core/models/Async';
import {flatten} from 'fp-ts/lib/Array';
import {pipe} from 'fp-ts/lib/pipeable';
import {fromJS, List as IList, Map as IMap} from 'immutable';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {QuotingToolModal, QuotingToolModalProps} from 'Quoting/components/Opportunity/QuoteActions/QuoteActionModals';
import {QuoteFormControlElement} from 'Quoting/components/shared/QuoteFormControlElement';
import {QuoteTextField} from 'Quoting/components/shared/QuoteTextField';
import {useInputLabelStyles} from 'Quoting/components/shared/styles';
import {
  changeSelectedLifeCoverage,
  editPlanName,
  removeQuoteFormErrorEntries,
  setSelectedCoverageIndex,
  updateOffer,
  updateQuoteRatesAndPremiumInformation,
  updateSelectedPlan,
} from 'Quoting/ducks/createQuote';
import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {getByKeyPath, isDefined, isQuoteNotEditable, VALUE_NOT_AVAILABLE} from 'Quoting/utils/utilities';
import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {createPlanCopy} from './copyPlanUtils';
import {GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP, GPLUCoverageCode} from './PlanTemplates/GWL/gpluConstants';
import {getCoverageDetailsObject, updateCoveragesfromPWL} from './PlanTemplates/GWL/GWLUtilities';
import {updateCensusClassOnPlanDeletion} from './products/Common/censusClassUtils';
import {findMandatoryCoverageCd} from './utils';

const usePlanActionModalStyles = makeStyles(() => ({
  planActionModal: {
    minHeight: '15rem',
    padding: '1rem 0',
    marginTop: '1rem',
  },
  oldPlanName: {
    fontWeight: 600,
    marginLeft: '0.25rem',
  },
  planNameAlert: {
    fontSize: '0.875rem',
  },
}));

export const enum PlanAction {
  Add = 'add',
  Copy = 'copy',
  Edit = 'edit',
  Delete = 'delete',
}

const PLAN_NAME_PREFIX = 'Plan';

const COVERAGE_ORDER = [
  GPLUCoverageCode.WHOLE_LIFE,
  GPLUCoverageCode.SPOUSE_WHOLE_LIFE,
  GPLUCoverageCode.CHILD_TERM_LIFE,
  GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE,
];

type PlanModalActionProps = Pick<QuotingToolModalProps, 'open' | 'onClose'> & {
  planAction: PlanAction;
  existingPlanNamesForQuote: string[];
  planName: string;
  planCode: string;
  planIndex: number;
  handlePlanAction: (planAction: PlanAction, newPlanName?: string) => void;
};

const planActionlabel = {
  [PlanAction.Add]: 'Add',
  [PlanAction.Copy]: 'Copy',
  [PlanAction.Delete]: 'Confirm deletion of',
  [PlanAction.Edit]: 'Edit',
};

const planNameRestrictionRegex = /^\d{1,2}$/;

const isValidPlanName = (planNameInput: string) => planNameRestrictionRegex.test(planNameInput);

const DUPLICATE_PLAN_NAME_ERROR = 'Plan with such name already exists. Please select a new one.';
const INVALID_PLAN_NAME_ERROR = 'Plan name is not allowed';

type CoverageItem = {
  code: string;
  name: string;
};

const PlanActionModal = (props: PlanModalActionProps) => {
  const dispatch = useDispatch();
  const {plansWithCoverages, selectedProduct, defaultPlanValues, quote} = useSelector(
    (state: ReducerState) => state.quoteForm
  );
  const [newPlanName, setNewPlanName] = useState<string>('');
  const [selectedCoveragesToAdd, setSelectedCoveragesToAdd] = useState<CoverageItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const inputLabelStyles = useInputLabelStyles();
  const classes = usePlanActionModalStyles();
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();

  const planCoverages = getQuoteFieldValue<IMap<string, any>>(
    `benefitPackagingDetail.plans[${props.planIndex ?? 0}].coverageDefs`
  );

  const isCoverageAction = [PlanAction.Add].includes(props.planAction);
  const isPlanNameChangeAction = [PlanAction.Edit, PlanAction.Copy].includes(props.planAction);

  const planActionLabel = planActionlabel[props.planAction] ?? '';

  const planActionTitle = !isCoverageAction ? (
    <Typography>
      {planActionLabel} Plan:
      <span className={classes.oldPlanName}>{props.planName}</span>
    </Typography>
  ) : (
    <Typography>Coverage Selection</Typography>
  );

  const getDeletedCoverages =
    [A360ProductCd.GPLU].includes(selectedProduct?.code) && isLoaded(plansWithCoverages)
      ? pipe(
          plansWithCoverages.Data,
          plans => plans.filter(p => p.planCode === props.planCode),
          plans => plans.map(p => p.coverages),
          flatten,
          coverages =>
            coverages.filter(
              coverage => coverage.applicability === 'Mandatory' || coverage.applicability === 'Optional'
            ),
          coverages =>
            coverages
              .filter(coverage => !planCoverages.some(res => res.get('code') === coverage.code))
              .map(coverage => ({
                code: coverage.code,
                name: coverage.name,
              }))
        )
      : null;

  const rebuildCoverageDefs = (
    planIndex: number,
    defaultValues: IMap<string, any>,
    rootId: string,
    parentId: string
  ) => {
    const existingCoverageDefs = getQuoteFieldValue<IMap<string, any>>(
      `benefitPackagingDetail.plans[${planIndex ?? 0}].coverageDefs`
    );

    const newObjects = fromJS(
      selectedCoveragesToAdd.map(cov => {
        const coverage = isLoaded(plansWithCoverages)
          ? pipe(
              plansWithCoverages.Data,
              plans => plans.filter(p => p.planCode === props.planCode),
              plans => plans.map(p => p.coverages),
              flatten,
              coverages =>
                coverages.filter(
                  coverage => coverage.applicability === 'Mandatory' || coverage.applicability === 'Optional'
                ),
              coverages => coverages.find(c => c.name === cov.name)
            )
          : null;
        const covObject = getCoverageDetailsObject(coverage.code, defaultValues, rootId, parentId);
        return updateCoveragesfromPWL(planIndex, quote, covObject);
      })
    );

    const merged = existingCoverageDefs.concat(newObjects);

    return merged.sort((a, b) => COVERAGE_ORDER.indexOf(a.get('code')) - COVERAGE_ORDER.indexOf(b.get('code')));
  };

  const handlePlanNameChange = useCallback(
    (planNameInput: string) => {
      setNewPlanName(planNameInput);
      if (isValidPlanName(planNameInput)) {
        setError(null);
      } else {
        setError(INVALID_PLAN_NAME_ERROR);
      }
    },
    [setNewPlanName, setError]
  );

  const handleClose = useCallback(() => {
    if ([PlanAction.Copy, PlanAction.Edit].includes(props.planAction)) {
      setNewPlanName('');
    }
    setSelectedCoveragesToAdd([]);
    setError(null);
    props.onClose?.();
  }, [newPlanName, error]);

  const handleContinue = useCallback(() => {
    const newFinalPlanName =
      newPlanName?.length === 2 ? `${PLAN_NAME_PREFIX}${newPlanName}` : `${PLAN_NAME_PREFIX} ${newPlanName}`;
    const selectedPlanCd =
      getQuoteFieldValue(`benefitPackagingDetail.plans[${props.planIndex ?? 0}].planCd`) ?? VALUE_NOT_AVAILABLE;

    if (!isCoverageAction) {
      if (props.existingPlanNamesForQuote?.includes(newFinalPlanName)) {
        setError(DUPLICATE_PLAN_NAME_ERROR);
      } else {
        props.handlePlanAction?.(props.planAction, newFinalPlanName);
        handleClose();
      }
    } else {
      updateQuoteFieldValue(
        `benefitPackagingDetail.plans[${props.planIndex ?? 0}].coverageDefs`,
        rebuildCoverageDefs(
          props.planIndex,
          defaultPlanValues.get(selectedPlanCd),
          getQuoteFieldValue(`benefitPackagingDetail.plans[${props.planIndex ?? 0}]._key.rootId`),
          getQuoteFieldValue(`benefitPackagingDetail.plans[${props.planIndex ?? 0}]._key.id`)
        )
      );
      dispatch(updateOffer());
      setSelectedCoveragesToAdd([]);
      handleClose();
    }
  }, [
    props.existingPlanNamesForQuote,
    props.handlePlanAction,
    props.planAction,
    props.onClose,
    newPlanName,
    setError,
    selectedCoveragesToAdd,
    defaultPlanValues,
    props.planIndex,
  ]);

  return (
    <QuotingToolModal
      title={planActionTitle}
      onContinue={handleContinue}
      onClose={handleClose}
      disableContinue={
        isCoverageAction
          ? selectedCoveragesToAdd.length <= 0
          : (isPlanNameChangeAction && newPlanName?.trim() === '') || isDefined(error)
      }
      open={props.open}
    >
      {!isCoverageAction && props.planAction === PlanAction.Delete && (
        <Typography>Are you sure you want to delete the plan: {props.planName}</Typography>
      )}
      {!isCoverageAction && isPlanNameChangeAction && (
        <>
          <Alert severity="info" className={classes.planNameAlert}>
            Plan name cannot be more than 6 characters. Please enter a valid plan name to Continue.
          </Alert>
          <div className={classes.planActionModal}>
            <InputLabel
              htmlFor={`${planActionLabel}-plan-${props.planName}`}
              className={inputLabelStyles.label}
              required
            >
              Plan Name
            </InputLabel>
            <QuoteTextField
              value={newPlanName}
              onChange={e => handlePlanNameChange(e.target.value)}
              id={`${planActionLabel}-plan-${props.planName}`}
              error={isDefined(error)}
              helperText={error ?? undefined}
              InputProps={{
                startAdornment: <InputAdornment position="start">Plan</InputAdornment>,
              }}
            />
          </div>
        </>
      )}
      {isCoverageAction && (
        <div>
          {Array.isArray(getDeletedCoverages) && getDeletedCoverages.length > 0 ? (
            <Grid container>
              {getDeletedCoverages.map(coverage => (
                <Grid item key={coverage.name} xs={12} sm={8} md={4} style={{margin: '1rem'}}>
                  <QuoteFormControlElement
                    controlType="checkbox"
                    id={coverage.name}
                    label={GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP[coverage.code as GPLUCoverageCode]}
                    checked={selectedCoveragesToAdd.some(c => c.code === coverage.code)}
                    key={coverage.name}
                    onChange={e =>
                      e.target.checked
                        ? setSelectedCoveragesToAdd(prev =>
                            prev.some(c => c.code === coverage.code) ? prev : [...prev, coverage]
                          )
                        : setSelectedCoveragesToAdd(prev => prev.filter(c => c.code !== coverage.code))
                    }
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}
        </div>
      )}
    </QuotingToolModal>
  );
};

type PlanActionMenuProps = {
  planIndex: number;
};

export const PlanActionMenu = ({planIndex}: PlanActionMenuProps) => {
  const [selectedPlanAction, setSelectedPlanAction] = useState<PlanAction | null>(null);

  const {isCalculatingRates, isSavingQuote, plansWithCoverages, selectedProduct, quoteFormErrors} = useSelector(
    (state: ReducerState) => state.quoteForm
  );

  const dispatch = useDispatch();

  const {getQuoteFieldValue, updateQuoteFieldValues, updateQuoteFieldValue, quote} = useQuoteField();

  const planName = getQuoteFieldValue(`benefitPackagingDetail.plans[${planIndex ?? 0}].planName`) ?? '';
  const planCode = getQuoteFieldValue(`benefitPackagingDetail.plans[${planIndex ?? 0}].planCd`) ?? '';

  const disablePlanActions = isQuoteNotEditable(quote) || isCalculatingRates || isSavingQuote;

  const numberOfExistingPlans = getQuoteFieldValue<IList<any>>('benefitPackagingDetail.plans')?.size;

  const disableCopyPlan =
    [A360ProductModel.GPLUMaster].includes(getQuoteFieldValue('_modelName')) && numberOfExistingPlans >= 4;

  const existingPlanNamesForQuote = getQuoteFieldValue<IList<any>>('benefitPackagingDetail.plans')?.reduce(
    (acc: string[], plan: IMap<string, any>) => [...acc, plan?.get('planName')],
    []
  );

  const isAddPresent = () => {
    const validCoverage = isLoaded(plansWithCoverages)
      ? pipe(
          plansWithCoverages.Data,
          plans => plans.filter(p => p.planCode === planCode),
          plans => plans.map(p => p.coverages),
          flatten,
          coverages =>
            coverages.filter(
              coverage => coverage.applicability === 'Mandatory' || coverage.applicability === 'Optional'
            )
        ).length >
        getQuoteFieldValue<IMap<string, any>>(`benefitPackagingDetail.plans[${planIndex ?? 0}].coverageDefs`).size
      : false;
    return [A360ProductCd.GPLU].includes(selectedProduct?.code) && validCoverage;
  };

  const handlePlanAction = (planAction: PlanAction, newPlanName?: string) => {
    switch (planAction) {
      case PlanAction.Edit: {
        const selectedPlanForAction: IMap<string, any> | undefined = getQuoteFieldValue(
          `benefitPackagingDetail.plans[${planIndex ?? 0}]`
        );
        if (selectedPlanForAction) {
          updateQuoteFieldValue(`benefitPackagingDetail.plans[${planIndex ?? 0}].planName`, newPlanName ?? '');
          dispatch(editPlanName());
        }
        return;
      }

      case PlanAction.Delete: {
        const planKey = getQuoteFieldValue(`benefitPackagingDetail.plans[${planIndex}]._key.id`);
        const globalErrorsToRemove = Object.keys(quoteFormErrors)?.filter(key => key.includes(planKey));

        const {plans, classGroups} = updateCensusClassOnPlanDeletion(quote.Data, planIndex);

        const fieldsToUpdate = [
          {
            path: 'benefitPackagingDetail.plans',
            newValue: plans,
          },
          {
            path: 'classGroups',
            newValue: classGroups,
          },
        ];

        updateQuoteFieldValues(fieldsToUpdate);

        if (planIndex > 0) {
          dispatch(
            updateSelectedPlan({
              planIndex: planIndex - 1,
              planKey: getQuoteFieldValue(`benefitPackagingDetail.plans[${planIndex - 1}]._key.id`),
            })
          );
        }

        dispatch(setSelectedCoverageIndex(0));
        dispatch(changeSelectedLifeCoverage(findMandatoryCoverageCd(plansWithCoverages, planCode)));

        dispatch(updateQuoteRatesAndPremiumInformation(undefined));

        // remove global errors related to the plan being deleted
        dispatch(removeQuoteFormErrorEntries(globalErrorsToRemove));
        return;
      }

      case PlanAction.Copy: {
        const selectedPlanForAction: IMap<string, any> | undefined = getQuoteFieldValue(
          `benefitPackagingDetail.plans[${planIndex ?? 0}]`
        );

        const lastPlanIndex = isDefined(numberOfExistingPlans) ? numberOfExistingPlans - 1 : null;
        const newPlanIndex = isDefined(lastPlanIndex) && lastPlanIndex >= 0 ? lastPlanIndex + 1 : 0;

        if (selectedPlanForAction) {
          const copiedPlan = createPlanCopy(selectedPlanForAction, selectedPlanForAction?.get('_key')?.get('parentId'));

          const updatedPlanList = getQuoteFieldValue<IList<any>>('benefitPackagingDetail.plans')?.push(copiedPlan);

          const fieldsToUpdate = [
            {
              path: 'benefitPackagingDetail.plans',
              newValue: updatedPlanList,
            },
            {
              path: `benefitPackagingDetail.plans[${newPlanIndex}].planName`,
              newValue: newPlanName ?? '',
            },
          ];
          if (updatedPlanList) {
            updateQuoteFieldValues(fieldsToUpdate);
            dispatch(
              updateSelectedPlan({
                planIndex: newPlanIndex,
                planKey: getByKeyPath(copiedPlan as IMap<string, any>, '_key.id') as string,
              })
            );
            dispatch(updateQuoteRatesAndPremiumInformation(undefined));
            dispatch(setSelectedCoverageIndex(0));
            dispatch(changeSelectedLifeCoverage(findMandatoryCoverageCd(plansWithCoverages, planCode)));
          }
        }

        return;
      }

      default:
        return;
    }
  };

  return (
    <>
      <ActionsMenu ariaLabel="Plan Action Menu">
        {isAddPresent() && (
          <MenuItem
            onClick={() => setSelectedPlanAction(PlanAction.Add)}
            disabled={disablePlanActions}
            aria-label="add coverage"
          >
            Add
          </MenuItem>
        )}
        <MenuItem
          onClick={() => setSelectedPlanAction(PlanAction.Copy)}
          disabled={disablePlanActions || disableCopyPlan}
          aria-label="copy plan"
        >
          Copy
        </MenuItem>
        <MenuItem
          onClick={() => setSelectedPlanAction(PlanAction.Edit)}
          disabled={disablePlanActions}
          aria-label="edit plan"
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => setSelectedPlanAction(PlanAction.Delete)}
          disabled={disablePlanActions}
          aria-label="delete plan"
        >
          Delete
        </MenuItem>
      </ActionsMenu>
      <PlanActionModal
        planAction={selectedPlanAction}
        handlePlanAction={handlePlanAction}
        open={isDefined(selectedPlanAction)}
        onClose={() => setSelectedPlanAction(null)}
        planName={planName}
        planCode={planCode}
        planIndex={planIndex}
        existingPlanNamesForQuote={existingPlanNamesForQuote}
      />
    </>
  );
};
